import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../hooks/use-toast";
import { getPaymentStructureLabel, formatDate, calculateVATInclusiveAmount } from "../../lib/appUtils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import {
  CheckCircle, XCircle, ArrowRight, Signature, Shield, CreditCard,
  Clock, Mail, Banknote, Info, Eye, AlertTriangle, Zap, TrendingUp,
  Calendar, FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientPaymentChoice = ({ project, provider, onChoiceComplete, onRefreshData, slugProp }) => {
  const { slug: slugFromParams } = useParams();
  const slug = slugProp || slugFromParams || project?.slug;
  
  const [selectedOption, setSelectedOption] = useState(null);
  const [showContract, setShowContract] = useState(false);
  const [isPaymentSaving, setIsPaymentSaving] = useState(false);
  const [contractContent, setContractContent] = useState('');
  const [showFullContractModal, setShowFullContractModal] = useState(false);
  const [signatureData, setSignatureData] = useState({
    full_name: '',
    email: '',
    position: '',
    company: '',
    agreed: false,
    identity_confirmed: false
  });
  const [processing, setProcessing] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending'); // 'pending' or 'verified'
  const { toast } = useToast();
  // Update signature data when project becomes available
  useEffect(() => {
    if (project) {
      setSignatureData(prev => ({
        ...prev,
        full_name: '',
        email: project.client_email || '',
        company: project.client_company || ''
      }));
    }
  }, [project]);

  const handlePaymentOptionSelect = async (option) => {
    try {
      setIsPaymentSaving(true);
      // CRITICAL FIX: Save payment selection to database IMMEDIATELY
      console.log('🔥 PAYMENT OPTION SELECTED:', option.id, 'for contract:', selectedProject?.slug);
      console.log('🔥 SLUG VALUE:', slug);
      console.log('🔥 PROJECT:', project);
      
      if (!slug) {
        console.error('❌ SLUG IS UNDEFINED!');
        toast({
          title: "Error",
          description: "Unable to process payment selection. Please try again.",
          variant: "destructive"
        });
        setIsPaymentSaving(false);
        return;
      }
      
      // Map frontend IDs to API format
      const apiOptionMap = {
        'fifty_fifty': 'FIFTY_FIFTY',
        'full_upfront': 'UPFRONT', 
        'three_month_milestones': 'MILESTONES',
        'credit_card': 'CREDIT_CARD',
        'bnpl': 'BNPL',
        'invoice': 'INVOICE'
      };
      
      const apiOption = apiOptionMap[option.id] || option.id.toUpperCase();
      
      const response = await axios.patch(`${API}/contracts/${slug}/payment`, {
        option: apiOption,
        bnpl_provider: option.id === 'bnpl' ? 'Klarna' : undefined,
        invoice_due_days: option.id === 'invoice' ? 14 : undefined
      });
      
      console.log('Payment selection saved successfully:', response.data);
      
      // CRITICAL: Wait for database to be fully updated before proceeding
      await new Promise(resolve => setTimeout(resolve, 1500)); // Wait 1.5 seconds
      
      // Only proceed to next step if API call succeeds
      setSelectedOption(option);
      
      toast({
        title: "Payment Option Saved",
        description: `${option.label} payment option has been saved and is ready.`,
      });
      
    } catch (error) {
      console.error('Failed to save payment selection:', error);
      console.error('Error details:', error.response?.data);
      toast({
        title: "Error",
        description: `Failed to save payment selection: ${error.response?.data?.detail || error.message}`,
        variant: "destructive"
      });
    } finally {
      setIsPaymentSaving(false);
    }
  };

  const paymentOptions = [
    {
      id: 'full_upfront',
      title: '100% Upfront',
      description: 'Pay the full amount now and start immediately',
      paymentDetails: `Full payment of £${project.total_amount.toLocaleString()} due upon contract signing. Card, Apple/Google Pay or Bank Transfer/Pay by Bank accepted. Work commences immediately.`,
      icon: <Banknote className="w-8 h-8" />,
      gradient: 'from-blue-400 to-blue-600'
    },
    {
      id: 'fifty_fifty',
      title: '50/50 Split',
      description: 'Pay half now, half on completion',
      paymentDetails: `Initial payment: £${calculateVATInclusiveAmount(project.total_amount * 0.5, project.vat_rate, project.vat_enabled).toLocaleString()} now. Final payment: £${calculateVATInclusiveAmount(project.total_amount * 0.5, project.vat_rate, project.vat_enabled).toLocaleString()} upon completion. Card or Apple/Google Pay accepted.`,
      icon: <TrendingUp className="w-8 h-8" />,
      gradient: 'from-green-400 to-green-600'
    },
    {
      id: 'three_month_milestones',
      title: 'Monthly Instalments',
      description: 'Pay over 3 months',
      paymentDetails: `1st payment: £${(project.total_amount * 0.4).toLocaleString()} (40%) now. 2nd: £${(project.total_amount * 0.3).toLocaleString()} (30%) month 1. 3rd: £${(project.total_amount * 0.3).toLocaleString()} (30%) month 2. Card or Apple/Google Pay accepted.`,
      icon: <Calendar className="w-8 h-8" />,
      gradient: 'from-purple-400 to-purple-600'
    },
    {
      id: 'credit_card',
      title: 'Credit Card',
      description: 'Pay the full amount on a credit card',
      paymentDetails: `Full payment of £${project.total_amount.toLocaleString()} charged to a credit card only — debit cards and bank transfers are not accepted on this option. Work commences immediately upon payment confirmation.`,
      icon: <CreditCard className="w-8 h-8" />,
      gradient: 'from-amber-400 to-amber-600'
    },
    {
      id: 'bnpl',
      title: 'Buy Now, Pay Later',
      subtitle: '(Klarna · Clearpay)',
      description: 'Pay in instalments — Klarna or Clearpay',
      paymentDetails: `Pay £${project.total_amount.toLocaleString()} in 3 interest-free instalments via Klarna or Clearpay. The full amount is released to ${provider.business_name} immediately upon approval; your lender handles the schedule with you. Subject to lender approval and your eligibility checks.`,
      icon: <Clock className="w-8 h-8" />,
      gradient: 'from-pink-400 to-pink-600',
    },
  ];

  const generatePaymentContract = (option) => {
    const baseContract = `
SERVICE AGREEMENT - ${project.title}

Provider: ${provider.business_name}
Client: ${project.client_name}
Total Amount: £${project.total_amount.toLocaleString()}
`;

    const paymentClauses = {
      full_upfront: `
PAYMENT TERMS: 100% Upfront
- Full payment of £${project.total_amount.toLocaleString()} due upon contract signing
- Work commences immediately upon payment confirmation
- Project completion within 30 days of payment
- No further payments required`,

      fifty_fifty: `
PAYMENT TERMS: 50/50 Split
- Initial payment: £${calculateVATInclusiveAmount(project.total_amount * 0.5, project.vat_rate, project.vat_enabled).toLocaleString()} (50%) due upon signing
- Final payment: £${calculateVATInclusiveAmount(project.total_amount * 0.5, project.vat_rate, project.vat_enabled).toLocaleString()} (50%) due upon completion
- Work commences upon first payment
- Final payment due within 7 days of delivery notification`,

      three_month_milestones: `
PAYMENT TERMS: Monthly Instalments
- 1st payment: £${(project.total_amount * 0.4).toLocaleString()} (40%) due upon signing
- 2nd payment: £${(project.total_amount * 0.3).toLocaleString()} (30%) due after month 1
- 3rd payment: £${(project.total_amount * 0.3).toLocaleString()} (30%) due after month 2
- Work progresses with each payment milestone`,

      credit_card: `
PAYMENT TERMS: Credit Card
- Full payment of £${project.total_amount.toLocaleString()} charged to a credit card upon contract signing
- Debit cards, bank transfers and digital wallets are not accepted on this option
- Work commences immediately upon successful card authorisation`,

      bnpl: `
PAYMENT TERMS: Buy Now Pay Later (via Klarna or Clearpay)
- The full amount of £${project.total_amount.toLocaleString()} will be released to the provider immediately upon lender approval
- You, the client, repay your chosen BNPL lender (Klarna or Clearpay) directly in 3 interest-free instalments or on your lender's terms
- Lender approval is subject to their eligibility checks; if declined, please choose another payment option
- Work commences immediately upon successful BNPL authorisation`
    };

    return baseContract + paymentClauses[option] + `

All amounts include VAT where applicable. This agreement is governed by the laws of England and Wales.

By signing, you agree to these specific payment terms and authorize commencement of work.`;
  };

  const handleOptionSelect = async (option) => {
    console.log('handleOptionSelect called with:', option);

    // Block "Coming Soon" options entirely — they exist for marketing only.
    if (option.coming_soon || option.disabled) {
      toast({ title: `${option.title} — coming soon`, description: 'Please pick another payment option for now.' });
      return;
    }

    // CRITICAL FIX: Save payment selection to database IMMEDIATELY
    try {
      console.log('🔥 SAVING PAYMENT SELECTION:', option.id, 'for project:', slug);
      
      // Map frontend IDs to backend API format
      const apiOptionMap = {
        'fifty_fifty': 'FIFTY_FIFTY',
        'full_upfront': 'UPFRONT',
        'three_month_milestones': 'MILESTONES', 
        'credit_card': 'CREDIT_CARD',
        'bnpl': 'BNPL',
        'invoice': 'INVOICE'
      };
      
      const apiOption = apiOptionMap[option.id] || option.id.toUpperCase();
      
      const response = await fetch(`${API}/contracts/${slug}/payment`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          option: apiOption,
          bnpl_provider: option.id === 'bnpl' ? 'Klarna' : undefined,
          invoice_due_days: option.id === 'invoice' ? 14 : undefined
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Payment selection saved successfully:', result);
      } else {
        console.error('❌ Failed to save payment selection:', response.status);
      }
    } catch (error) {
      console.error('❌ Error saving payment selection:', error);
    }
    
    setSelectedOption(option);
    console.log('setSelectedOption called');
    setContractContent(generatePaymentContract(option.id));
    console.log('setContractContent called');
    setShowContract(true);
    console.log('setShowContract(true) called');
  };

  const handleContractSign = async (e) => {
    e.preventDefault();
    if (!signatureData.agreed || !signatureData.full_name.trim()) {
      return;
    }

    setProcessing(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const flowParam = urlParams.get('flow');
      
      const apiUrl = `${API}/checkout/${slug}/choose-payment${flowParam ? `?flow=${flowParam}` : ''}`;
      
      await axios.post(apiUrl, {
        payment_structure: selectedOption.id,
        full_name: signatureData.full_name.trim(),
        agreed: signatureData.agreed,
        ip_address: '127.0.0.1',
        user_agent: navigator.userAgent
      });

      // Redirect to checkout without flow parameter
      const url = new URL(window.location);
      url.searchParams.delete('flow');
      window.location.href = url.toString();
    } catch (error) {
      console.error('Contract signing error:', error);
      
      // Handle different error formats
      let errorMessage = "Please try again";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // If detail is a string, use it
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // If detail is an array of validation errors, extract the message
        else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err.message || 'Validation error').join(', ');
        }
        // If detail is an object with message
        else if (errorData.detail?.msg) {
          errorMessage = errorData.detail.msg;
        }
        // Fallback to generic message or error string
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast({
        title: "Signing Failed",
        description: errorMessage,
        variant: "destructive"
      });
      setProcessing(false);
    }
  };

  const handleEnhancedContractSign = async (e) => {
    e.preventDefault();
    if (!signatureData.agreed || !signatureData.identity_confirmed || !signatureData.full_name?.trim() || !signatureData.email?.trim() || !signatureData.position?.trim()) {
      toast({
        title: "Required Information Missing",
        description: "Please complete all required fields and confirmations, including your position/title",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      // Generate device fingerprint for audit trail
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      
      const deviceFingerprint = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screen: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        canvas: canvas.toDataURL(),
        timestamp: new Date().toISOString(),
        payment_structure: selectedOption.id  // CRITICAL FIX: Include payment structure in device fingerprint
      };

      const contractHash = btoa(JSON.stringify({
        slug: slug,
        payment_structure: selectedOption.id,
        signer_name: signatureData.full_name,
        signer_email: signatureData.email,
        timestamp: deviceFingerprint.timestamp
      }));

      const urlParams = new URLSearchParams(window.location.search);
      const flowParam = urlParams.get('flow');
      
      const apiUrl = `${API}/checkout/${slug}/choose-payment-enhanced${flowParam ? `?flow=${flowParam}` : ''}`;
      
      // STEP 1: Authoritative payment option binding
      // Map frontend IDs to backend API format
      const apiOptionMap = {
        'fifty_fifty': 'FIFTY_FIFTY',
        'full_upfront': 'UPFRONT',
        'three_month_milestones': 'MILESTONES', 
        'credit_card': 'CREDIT_CARD',
        'bnpl': 'BNPL',
        'invoice': 'INVOICE'
      };
      
      const apiOption = apiOptionMap[selectedOption.id] || selectedOption.id.toUpperCase();
      
      const paymentUpdatePayload = {
        option: apiOption,
        bnpl_provider: selectedOption.id === 'bnpl' ? 'Klarna' : undefined,
        invoice_due_days: selectedOption.id === 'invoice' ? 14 : undefined
      };
      
      const paymentUpdateResponse = await axios.patch(
        `${API}/contracts/${slug}/payment`, 
        paymentUpdatePayload
      );
      
      // STEP 2: Sign contract only after payment option is bound
      const signaturePayload = {
        payment_structure: selectedOption.id,
        full_name: signatureData.full_name.trim(),
        email: signatureData.email.trim(),
        position: signatureData.position?.trim() || '',
        agreed: signatureData.agreed,
        identity_confirmed: signatureData.identity_confirmed,
        device_fingerprint: deviceFingerprint,
        contract_hash: contractHash,
        ip_address: '127.0.0.1',
        user_agent: navigator.userAgent,
        signature_method: 'enhanced_electronic_uk_compliant'
      };

      await axios.post(apiUrl, signaturePayload);

      // Set verification sent state to show waiting screen
      setVerificationSent(true);
      setVerificationStatus('pending');
      
      toast({
        title: "Signature Captured Successfully",
        description: `Email verification has been sent to ${signatureData.email}. Please check your inbox and click the verification link to complete the signing process.`,
      });

      // Poll for verification completion
      const checkVerification = setInterval(async () => {
        try {
          if (onRefreshData) {
            const result = await onRefreshData();
            if (result?.data?.project?.contract_agreed === true) {
              clearInterval(checkVerification);
              setVerificationStatus('verified');
              // Wait 2 seconds to show verified state, then refresh
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            }
          }
        } catch (error) {
          // Continue polling
        }
      }, 5000);

      // Stop polling after 10 minutes
      setTimeout(() => clearInterval(checkVerification), 600000);

    } catch (error) {
      console.error('Enhanced contract signing error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = "Please try again";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Signature Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  console.log('ClientPaymentChoice render - showContract:', showContract, 'selectedOption:', selectedOption);
  
  // Verification Pending state - show waiting screen after signing
  if (verificationSent) {
    const isVerified = verificationStatus === 'verified';
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              {isVerified ? (
                <CheckCircle className="w-16 h-16 text-green-500" />
              ) : (
                <Mail className="w-16 h-16 text-amber-500" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">
              {isVerified ? 'Email Verified!' : 'Check Your Email'}
            </h1>
            <p className="text-gray-600 font-body mb-6">
              {isVerified 
                ? 'Your signature has been verified successfully.' 
                : "We've sent you a verification email."}
            </p>
            {isVerified ? (
              <p className="text-sm text-green-600 font-body mb-6">
                Redirecting to payment options...
              </p>
            ) : (
              <p className="text-sm text-gray-500 font-body mb-6">
                Click the verification link in the email to complete signing the contract. This page will automatically update once verified.
              </p>
            )}
            {isVerified && (
              <div className="flex items-center justify-center space-x-2 mb-6 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-body">Verified</span>
              </div>
            )}
            {!isVerified && (
              <div className="border-t border-gray-200 pt-4">
                <p className="text-xs text-gray-400 font-body">
                  Didn't receive the email? Check your spam folder or contact support.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-400 via-pink-500 to-pink-600 p-4">
      <div className="max-w-md mx-auto">
        {!showContract ? (
          /* Payment Option Selection - Mobile Design */
          <div className="text-center text-white">
            {/* Header */}
            <div className="flex items-center mb-8">
              <h1 className="text-2xl font-bold font-heading">Paify</h1>
            </div>

            {/* Title */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-2 font-heading">
                Decide how you want to pay.
              </h2>
            </div>

            {/* Payment Cards — 2-column grid; BNPL spans both columns on the third row */}
            <div className="grid grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
              {paymentOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleOptionSelect(option)}
                  disabled={option.coming_soon || option.disabled}
                  data-testid={`payment-option-${option.id}`}
                  className={`relative bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl p-5 text-center transition-all ${
                    option.coming_soon || option.disabled
                      ? 'opacity-60 cursor-not-allowed grayscale-[35%]'
                      : 'hover:bg-opacity-30 hover:scale-105 cursor-pointer'
                  } ${option.id === 'bnpl' || option.coming_soon ? 'col-span-2' : ''}`}
                >
                  {/* "Coming soon" ribbon — only on disabled options */}
                  {option.coming_soon && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-yellow-400 text-gray-900 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full shadow-md whitespace-nowrap" data-testid={`payment-option-coming-soon-${option.id}`}>
                      Coming Soon
                    </div>
                  )}

                  {/* Content */}
                  <div className="text-white">
                    <h3 className="text-base lg:text-lg font-bold mb-1 font-heading">
                      {option.title}
                    </h3>
                    {option.subtitle && (
                      <p className="text-xs opacity-90 font-body mb-2">
                        {option.subtitle}
                      </p>
                    )}
                    <p className="text-xs opacity-80 font-body leading-tight">
                      {option.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* Bottom Text */}
            <div className="text-center mb-8">
              <p className="text-white text-lg font-body">
                Pick the payment style that fits you.
              </p>
              <p className="text-white text-lg font-body">
                We handle the rest.
              </p>
            </div>
          </div>
        ) : (
          /* Contract Review & Signing - Streamlined */
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowContract(false);
                  setSelectedOption(null);
                }}
                className="text-gray-600 font-body"
              >
                ← Back
              </Button>
              <h2 className="text-lg font-bold text-gray-900 font-heading">
                {selectedOption.title}
              </h2>
            </div>

            {/* Payment Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2 font-heading">{project.title}</h3>
              <p className="text-sm text-gray-600 font-body mb-3">{selectedOption.paymentDetails}</p>
              {project.vat_enabled ? (
                <div className="space-y-1">
                  <div className="flex justify-between text-sm text-gray-600 font-body">
                    <span>Subtotal:</span>
                    <span>£{project.total_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 font-body">
                    <span>VAT ({project.vat_rate}%):</span>
                    <span>£{(project.total_amount * (project.vat_rate / 100)).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-gray-900 font-heading border-t pt-1">
                    <span>Total (inc VAT):</span>
                    <span>£{(project.total_amount + (project.total_amount * (project.vat_rate / 100))).toLocaleString()}</span>
                  </div>
                </div>
              ) : (
                <div className="text-xl font-bold text-gray-900 font-heading">
                  Total: £{project.total_amount.toLocaleString()}
                </div>
              )}
            </div>

            {/* Contract Terms */}
            <div className="mb-6">
            {/* Service Agreement View Button */}
            <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900 font-heading">Service Agreement</h4>
                  <p className="text-sm text-gray-600">Review the complete service contract before signing</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={isPaymentSaving}
                    onClick={() => {
                      if (isPaymentSaving) return;
                      setShowFullContractModal(true);
                    }}
                    className="flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4" />
                    <span>{isPaymentSaving ? 'Saving Selection...' : (project?.contract_signed ? 'View Signed Contract' : 'View Contract PDF')}</span>
                  </Button>
                  
                  {project?.contract_signed && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `${API}/contracts/${slug}/signed-pdf`;
                        link.download = `${project.title}_Signed_Contract.html`;
                        link.click();
                      }}
                      className="flex items-center space-x-2 bg-green-50 hover:bg-green-100 border-green-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span>Download</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
            </div>

            {/* UK Government Compliant Electronic Signature */}
            <div className="space-y-4">
              {/* Security Badge */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-blue-900 font-body">UK Government Compliant Electronic Signature</span>
                </div>
                <p className="text-xs text-blue-700 font-body">
                  Secure electronic signature with email verification, audit trail, and legal compliance for UK government contracts.
                </p>
              </div>

              <form onSubmit={handleEnhancedContractSign} className="space-y-4">
                <div>
                  <Label className="font-body text-sm">Full Legal Name *</Label>
                  <Input
                    value={signatureData.full_name}
                    onChange={(e) => setSignatureData({ ...signatureData, full_name: e.target.value })}
                    placeholder="Enter your full legal name as it appears on official documents"
                    className="font-body"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1 font-body">
                    This name will be used for legal verification and must match official records.
                  </p>
                </div>

                <div>
                  <Label className="font-body text-sm">Email Address (For Verification) *</Label>
                  <Input
                    type="email"
                    value={signatureData.email || project.client_email || ''}
                    onChange={(e) => setSignatureData({ ...signatureData, email: e.target.value })}
                    placeholder="Enter your email address for signature verification"
                    className="font-body"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1 font-body">
                    A verification email will be sent to confirm your signature.
                  </p>
                </div>

                <div>
                  <Label className="font-body text-sm">Position/Title *</Label>
                  <Input
                    value={signatureData.position || ''}
                    onChange={(e) => setSignatureData({ ...signatureData, position: e.target.value })}
                    placeholder="e.g., Director, Managing Partner, Authorized Representative"
                    className="font-body"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1 font-body">
                    Required to establish signing authority for government contracts.
                  </p>
                </div>
                
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="identity_checkbox"
                      checked={signatureData.identity_confirmed || false}
                      onCheckedChange={(checked) => setSignatureData({ ...signatureData, identity_confirmed: !!checked })}
                    />
                    <Label htmlFor="identity_checkbox" className="text-xs leading-4 font-body cursor-pointer">
                      I confirm that I am the person named above and have the authority to sign this agreement on behalf of the organization (if applicable)
                    </Label>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agreement_checkbox"
                      checked={signatureData.agreed}
                      onCheckedChange={(checked) => setSignatureData({ ...signatureData, agreed: !!checked })}
                    />
                    <Label htmlFor="agreement_checkbox" className="text-xs leading-4 font-body cursor-pointer">
                      I agree to the terms with <strong>{selectedOption.title}</strong> payment and authorize electronic signature processing in accordance with UK electronic signature regulations
                    </Label>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={processing || !signatureData.agreed || !signatureData.identity_confirmed || !signatureData.full_name?.trim() || !signatureData.email?.trim() || !signatureData.position?.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold font-body h-12"
                >
                  {processing ? "Processing Signature..." : `Sign Agreement & Send Email Verification`}
                </Button>
              </form>
              
              <div className="text-xs text-gray-500 font-body border-t pt-3">
                <p className="mb-1"><strong>What happens next:</strong></p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Your signature will be recorded with full audit trail</li>
                  <li>Email verification will be sent to the address provided</li>
                  <li>Click the verification link to complete the signing process</li>
                  <li>Work begins immediately upon email verification</li>
                </ol>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-600 font-body">
                By signing, work begins immediately. Payment processed according to selected terms.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contract View Dialog */}
      <Dialog open={showFullContractModal} onOpenChange={setShowFullContractModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Service Agreement</DialogTitle>
          </DialogHeader>
          <iframe
            src={project?.contract_signed 
              ? `${API}/contracts/${slug}/signed-pdf` 
              : `${API}/contracts/${slug}/pdf?v=${Date.now()}`}
            className="w-full h-[75vh] border-0"
            title="Service Agreement"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
// Verification Pages

export default ClientPaymentChoice;
